import React, { useState } from "react";
import {
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import CreditCardIcon from "@material-ui/icons/CreditCard";
import { green } from "@material-ui/core/colors";
import { useVoice } from "../../context/Voice/VoiceContext";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

/**
 * Quick Charge — close the deal without leaving the conversation. Opens a tiny
 * dialog (amount + reason), creates the payment request + Tranzila checkout
 * link in one call, and lets the agent send it straight into the chat.
 * Renders nothing unless Voice/sales is enabled for the company (same gate as
 * the call button).
 */
const QuickChargeButton = ({ ticket }) => {
  const { status } = useVoice();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  if (!status || !status.enabled) return null;

  const contact = ticket && ticket.contact;
  const t = (k) => i18n.t(`quickCharge.${k}`);

  const reset = () => {
    setAmount("");
    setReason("");
    setResult(null);
  };

  const handleCharge = async () => {
    const amt = parseFloat(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error(t("invalidAmount"));
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/voice/quick-charge", {
        amount: amt,
        reason: reason || t("defaultReason"),
        contactId: contact?.id,
        ticketId: ticket?.id,
      });
      setResult(data);
      if (!data.linkAvailable) toast.info(t("noLinkButCreated"));
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const sendToChat = async () => {
    if (!result?.checkoutUrl || !ticket?.id) return;
    try {
      const message = `${t("chatPrefix")} ${result.amount} ${result.currency}\n${result.checkoutUrl}`;
      await api.post(`/messages/${ticket.id}`, {
        body: message,
        fromMe: true,
        read: true,
      });
      toast.success(t("sent"));
      close();
    } catch (err) {
      toastError(err);
    }
  };

  const copyLink = () => {
    if (!result?.checkoutUrl) return;
    // navigator.clipboard is undefined on insecure (http) origins.
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(result.checkoutUrl);
      toast.success(t("copied"));
    } else {
      toast.info(result.checkoutUrl);
    }
  };

  const close = () => {
    setOpen(false);
    reset();
  };

  return (
    <>
      <Tooltip title={t("button")}>
        <span>
          <IconButton
            onClick={() => setOpen(true)}
            style={{ color: green[700], padding: 6 }}
            aria-label={t("button")}
          >
            <CreditCardIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Dialog open={open} onClose={close} fullWidth maxWidth="xs">
        <DialogTitle>{t("title")}</DialogTitle>
        <DialogContent>
          {!result ? (
            <>
              {contact?.name && (
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {t("customer")}: {contact.name}
                </Typography>
              )}
              <TextField
                autoFocus
                fullWidth
                type="number"
                margin="dense"
                variant="outlined"
                label={t("amount")}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
              <TextField
                fullWidth
                margin="dense"
                variant="outlined"
                label={t("reason")}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t("defaultReason")}
              />
            </>
          ) : (
            <>
              <Typography variant="subtitle1" gutterBottom>
                {t("created")} — {result.amount} {result.currency}
              </Typography>
              {result.checkoutUrl ? (
                <>
                  <TextField
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    value={result.checkoutUrl}
                    InputProps={{ readOnly: true }}
                  />
                  <Typography variant="caption" color="textSecondary">
                    {t("linkHint")}
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="error">
                  {t("noLink")}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {!result ? (
            <>
              <Button onClick={close} disabled={loading}>
                {t("cancel")}
              </Button>
              <Button
                onClick={handleCharge}
                color="primary"
                variant="contained"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} /> : <CreditCardIcon />}
              >
                {t("create")}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={close}>{t("done")}</Button>
              {result.checkoutUrl && (
                <>
                  <Button onClick={copyLink}>{t("copy")}</Button>
                  <Button onClick={sendToChat} color="primary" variant="contained">
                    {t("sendToChat")}
                  </Button>
                </>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default QuickChargeButton;
